<script setup lang="ts">
import { computed, nextTick, ref, toRef, watch } from "vue";
import type { PaginationParams } from "@/types";
import { useGetReportTemplates } from "@/stores/queries/report-templates.query";
import type { ReportTemplate } from "@/types/report-templates.types";
import TemplateIcon from "@/components/UI/icons/TemplateIcon.vue";
import ReportState, { ReportSection } from "@/stores/report/ReportState";
import { useUpdateReport } from "@/stores/queries";
import SkeletonList from "@/components/UI/skeleton/SkeletonList.vue";

const props = defineProps<{
  visible: boolean;
  reportId?: number;
}>();

const emit = defineEmits<{
  (e: "update:visible", value: boolean): void;
  (e: "close"): void;
}>();

const reportId = toRef(props, "reportId");
const searchInputRef =
  ref<InstanceType<typeof import("element-plus").ElInput>>();

const dialogVisible = computed({
  get: () => props.visible,
  set: (value) => emit("update:visible", value),
});

const query = ref("");
const page = ref(1);
const rowsPerPage = 10;

const pagination = computed<PaginationParams>(() => ({
  skip: (page.value - 1) * rowsPerPage,
  take: rowsPerPage,
  sortBy: "createdAt",
  order: "desc",
}));

const filters = computed(() => {
  const f: Record<string, any> = {};
  if (query.value) {
    f.search = query.value;
  }
  return f;
});

const { data, isLoading } = useGetReportTemplates(pagination, filters);
const { mutateAsync: updateReport, isPending: isUpdating } =
  useUpdateReport(reportId);

const templates = computed<ReportTemplate[]>(() => data.value?.templates ?? []);
const totalCount = computed<number>(() => data.value?.totalCount ?? 0);

const selectTemplate = (template: ReportTemplate) => {
  updateReport(
    { templateId: template.id },
    {
      onSuccess: () => {
        ReportState.setActiveTemplate(template.id);
        if (ReportState.activeSection === ReportSection.SELECT_TEMPLATE) {
          ReportState.setActiveSection(ReportSection.GENERAL);
        }
        dialogVisible.value = false;
        emit("close");
      },
    },
  );
};

watch(
  () => props.visible,
  (visible) => {
    if (visible) {
      query.value = "";
      page.value = 1;
      // Autofocus sur l'input de recherche
      nextTick(() => {
        searchInputRef.value?.focus();
      });
    }
  },
);
</script>

<template>
  <el-dialog
    v-model="dialogVisible"
    :title="$t('report:report.template.select')"
    width="600px"
    destroy-on-close
    @close="emit('close')"
  >
    <div v-loading="isUpdating">
      <el-input
        ref="searchInputRef"
        v-model="query"
        :placeholder="$t('search')"
        clearable
        size="large"
        class="search-input mb-4"
      >
        <template #prefix>
          <el-icon size="20"><i class="bi-search"></i></el-icon>
        </template>
      </el-input>

      <div v-if="isLoading">
        <SkeletonList :count="5" height="3.5rem" />
      </div>

      <div v-else-if="templates.length" class="template-list">
        <div
          v-for="template in templates"
          :key="template.id"
          class="template-item d-flex align-items-center gap-3 p-3 rounded cursor-pointer"
          :class="{
            'bg-primary-light border-primary':
              ReportState.activeTemplateId === template.id,
          }"
          @click="selectTemplate(template)"
        >
          <TemplateIcon :size="36" />
          <div class="flex-grow-1 text-truncate">
            <div class="fw-medium fs-6">{{ template.name }}</div>
          </div>
          <el-icon
            v-if="ReportState.activeTemplateId === template.id"
            class="text-primary"
            size="24"
          >
            <i class="bi-check-lg"></i>
          </el-icon>
        </div>
      </div>

      <div v-else class="text-center text-muted py-4">
        {{ $t("report:no.catalog.found") }}
      </div>

      <div
        v-if="totalCount > rowsPerPage"
        class="d-flex justify-content-center mt-4"
      >
        <el-pagination
          v-model:current-page="page"
          :page-size="rowsPerPage"
          background
          layout="prev, pager, next"
          :total="totalCount"
        />
      </div>
    </div>
  </el-dialog>
</template>

<style lang="scss" scoped>
.search-input {
  :deep(.el-input__wrapper) {
    border-radius: 12px;
    padding: 4px 16px;
    box-shadow: 0 0 0 1px var(--el-border-color) inset;

    &:hover,
    &:focus-within {
      box-shadow: 0 0 0 1px var(--el-color-primary) inset;
    }
  }

  :deep(.el-input__inner) {
    font-size: 16px;
  }
}

.template-list {
  max-height: 400px;
  overflow-y: auto;
}

.template-item {
  border: 1px solid transparent;
  transition: all 0.2s;

  &:hover {
    background-color: var(--bs-light);
  }
}
</style>
